import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import './App.css';

// Components
import NavigationBar from './components/NavigationBar';
import HeroSection from './components/HeroSection';
import StatsSection from './components/StatsSection';
import AboutUs from './components/AboutUs';
import Footer from './components/Footer';
import Contact from './components/Contact';
import BackToTop from './components/BackToTop';
import Partners from './components/Partners';
import Testimonials from './components/Testimonials';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react"

// Lazy loaded routes for better performance (Phase 6)
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));

// Loading component for Suspense
const PageLoader = () => (
  <div className="page-loader">
    <div className="loader-spinner"></div>
    <p>Loading...</p>
  </div>
);

function App() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('bvusa-theme');
    if (savedTheme) return savedTheme;
    
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('bvusa-theme', theme);
  }, [theme]);

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }, [menuOpen]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const openContact = () => setIsContactOpen(true);
  const closeContact = () => setIsContactOpen(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <Router>
      <div className={`App ${theme}`}>
        <a href="#main-content" className="skip-link">Skip to main content</a>
        
        <NavigationBar 
          handleContactClick={openContact} 
          theme={theme} 
          toggleTheme={toggleTheme} 
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />
        
        <main id="main-content">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <HeroSection />
                    <Partners />
                    <StatsSection />
                    <AboutUs />
                    <Testimonials />
                  </>
                }
              />
              <Route 
                path="/terms-and-conditions" 
                element={<TermsAndConditions theme={theme} />} 
              />
            </Routes>
          </Suspense>
        </main>

        <Footer handleContactClick={openContact} />
        
        <Contact isOpen={isContactOpen} onClose={closeContact} theme={theme} />
        <BackToTop closeMenu={closeMenu} />
        <SpeedInsights />
        <Analytics />
      </div>
    </Router>
  );
}

export default App;
